// Hosts whose favicon may be rendered next to external links in user-generated
// content. The favicon is a visual cue that says "this is the real site, not an
// impersonator" — NOT an editorial endorsement.
//
// Inclusion criteria: well-known, broadly safe-to-click destinations. Mainstream
// news (per Wikipedia's "Generally Reliable" perennial-sources list), reference
// works, major platforms, sports, science/gov bodies. Workplace-safe.
//
// To propose additions: open a PR adding the bare hostname to the appropriate
// section below. Use the registrable domain (e.g. `bbc.com`, not
// `www.bbc.com`); subdomains match automatically.

export const FAVICON_ALLOWED_HOSTS = new Set<string>([
  // ===== News & journalism (Wikipedia "Generally Reliable") =====
  'abcnews.go.com',
  'theage.com.au',
  'afp.com',
  'aljazeera.com',
  'adl.org',
  'arstechnica.com',
  'apnews.com',
  'theatlantic.com',
  'theaustralian.com.au',
  'avclub.com',
  'axios.com',
  'bbc.com',
  'bbc.co.uk',
  'bellingcat.com',
  'bloomberg.com',
  'csmonitor.com',
  'buzzfeednews.com',
  'cnet.com',
  'cnn.com',
  'commonsensemedia.org',
  'theconversation.com',
  'dailydot.com',
  'telegraph.co.uk',
  'deadline.com',
  'deseret.com',
  'digitalspy.com',
  'thediplomat.com',
  'economist.com',
  'engadget.com',
  'ew.com',
  'ft.com',
  'forbes.com',
  'foxnews.com',
  'gamedeveloper.com',
  'gameinformer.com',
  'gizmodo.com',
  'theglobeandmail.com',
  'theguardian.com',
  'guardian.co.uk',
  'haaretz.com',
  'thehill.com',
  'thehindu.com',
  'hollywoodreporter.com',
  'huffpost.com',
  'ign.com',
  'independent.co.uk',
  'indianexpress.com',
  'businessinsider.com',
  'ipsnews.net',
  'theintercept.com',
  'poynter.org',
  'jamanetwork.com',
  'thejc.com',
  'kirkusreviews.com',
  'kommersant.ru',
  'latimes.com',
  'mg.co.za',
  'metacritic.com',
  'monde-diplomatique.fr',
  'motherjones.com',
  'msnbc.com',
  'thenation.com',
  'nationalgeographic.com',
  'nbcnews.com',
  'newrepublic.com',
  'newscientist.com',
  'nymag.com',
  'nydailynews.com',
  'nytimes.com',
  'newyorker.com',
  'nzherald.co.nz',
  'newsweek.com',
  'npr.org',
  'people.com',
  'pewresearch.org',
  'pinknews.co.uk',
  'politico.com',
  'politifact.com',
  'polygon.com',
  'propublica.org',
  'qz.com',
  'rferl.org',
  'rappler.com',
  'reason.com',
  'theregister.com',
  'religionnews.com',
  'reuters.com',
  'rollingstone.com',
  'rottentomatoes.com',
  'sciencebasedmedicine.org',
  'scientificamerican.com',
  'scotusblog.com',
  'slate.com',
  'snopes.com',
  'splcenter.org',
  'scmp.com',
  'space.com',
  'spiegel.de',
  'smh.com.au',
  'thewrap.com',
  'time.com',
  'thetimes.co.uk',
  'torrentfreak.com',
  'tvguide.com',
  'usnews.com',
  'usatoday.com',
  'vanityfair.com',
  'variety.com',
  'venturebeat.com',
  'vice.com',
  'vogue.com',
  'vox.com',
  'wsj.com',
  'washingtonpost.com',
  'wired.com',
  'zdnet.com',

  // ===== Reference & encyclopedic =====
  'wikipedia.org',
  'wiktionary.org',
  'wikidata.org',
  'wikimedia.org',
  'wikisource.org',
  'wikiquote.org',
  'britannica.com',
  'merriam-webster.com',
  'dictionary.com',
  'imdb.com',
  'boxofficemojo.com',
  'goodreads.com',
  'liquipedia.net',

  // ===== Academic & scientific =====
  'arxiv.org',
  'ssrn.com',
  'nature.com',
  'science.org',
  'sciencedirect.com',
  'ncbi.nlm.nih.gov',
  'pubmed.ncbi.nlm.nih.gov',
  'jstor.org',
  'plos.org',

  // ===== Government & intergovernmental =====
  'nih.gov',
  'cdc.gov',
  'fda.gov',
  'nasa.gov',
  'usgs.gov',
  'whitehouse.gov',
  'state.gov',
  'treasury.gov',
  'sec.gov',
  'congress.gov',
  'supremecourt.gov',
  'gov.uk',
  'parliament.uk',
  'europa.eu',
  'un.org',
  'who.int',
  'nato.int',
  'esa.int',
  'oecd.org',
  'worldbank.org',
  'imf.org',

  // ===== Think tanks & research =====
  'brookings.edu',
  'rand.org',
  'mit.edu',
  'stanford.edu',
  'harvard.edu',
  'ox.ac.uk',
  'cam.ac.uk',

  // ===== Prediction markets & forecasting =====
  'polymarket.com',
  'kalshi.com',
  'metaculus.com',
  'goodjudgment.com',
  'predictit.org',
  'fivethirtyeight.com',
  'silverbulletin.com',

  // ===== Social platforms =====
  'x.com',
  'twitter.com',
  'facebook.com',
  'instagram.com',
  'threads.net',
  'bsky.app',
  'mastodon.social',
  'reddit.com',
  'linkedin.com',
  'tiktok.com',
  'quora.com',

  // ===== Video, audio, media =====
  'youtube.com',
  'youtu.be',
  'vimeo.com',
  'twitch.tv',
  'spotify.com',
  'soundcloud.com',
  'podcasts.apple.com',

  // ===== Dev & technical =====
  'github.com',
  'gitlab.com',
  'gist.github.com',
  'stackoverflow.com',
  'stackexchange.com',

  // ===== Productivity & messaging =====
  'discord.com',
  'discord.gg',
  'slack.com',
  'notion.so',
  'whatsapp.com',
  'telegram.org',
  'signal.org',

  // ===== Publishing platforms =====
  'substack.com',
  'medium.com',

  // ===== Big tech (corporate) =====
  'apple.com',
  'microsoft.com',
  'google.com',
  'docs.google.com',
  'drive.google.com',
  'scholar.google.com',
  'maps.google.com',
  'amazon.com',
  'ebay.com',
  'steampowered.com',

  // ===== Sports — leagues & governing bodies =====
  'nba.com',
  'wnba.com',
  'nfl.com',
  'mlb.com',
  'nhl.com',
  'mls.com',
  'ncaa.com',
  'fifa.com',
  'uefa.com',
  'premierleague.com',
  'laliga.com',
  'bundesliga.com',
  'legaseriea.it',
  'ligue1.com',
  'afl.com.au',
  'nrl.com',
  'cricket.com.au',
  'ecb.co.uk',
  'icc-cricket.com',
  'olympics.com',
  'ioc.org',
  'paralympic.org',
  'formula1.com',
  'nascar.com',
  'indycar.com',
  'motogp.com',
  'worldrugby.org',
  'ufc.com',
  'atptour.com',
  'wtatennis.com',
  'itftennis.com',
  'pgatour.com',
  'europeantour.com',
  'iihf.com',
  'fide.com',
  'iplt20.com',

  // ===== Sports tournaments =====
  'ausopen.com',
  'rolandgarros.com',
  'wimbledon.com',
  'usopen.org',
  'esportsworldcup.com',
  'grandchesstour.org',
  'norwaychess.no',

  // ===== Sports media =====
  'espn.com',
  'theathletic.com',
  'bleacherreport.com',
  'skysports.com',
  'cbssports.com',
  'foxsports.com',
  'nbcsports.com',
  'espncricinfo.com',
  'cricbuzz.com',

  // ===== Other (currently uncategorized) =====
  'imo-official.org',
  'eurovision.com',
  'chess.com'
])

// Returns true if `hostname` is in the allowlist, OR is a subdomain of an
// allowlisted host. e.g. `www.bbc.com` and `news.bbc.com` both match `bbc.com`.
export const isFaviconAllowed = (hostname: string): boolean => {
  const parts = hostname.split('.')
  for (let i = 0; i < parts.length - 1; i++) {
    const suffix = i === 0 ? hostname : parts.slice(i).join('.')
    if (FAVICON_ALLOWED_HOSTS.has(suffix)) return true
  }
  return false
}
